import { IdAndName } from "@/api/IdAndName";
import { LabelAndValueOption } from "@/data/LabelAndValueOption";
import { useCallback, useEffect, useMemo, useState } from "react";

type NameListLoader = () => Promise<IdAndName[]>;

type NameListQueryConfig = {
  queryKey: string;
  query: NameListLoader;
  enabled?: boolean;
};

type NameListState = {
  names: IdAndName[];
  isLoading: boolean;
  error: Error | null;
};

const pendingNameListQueries = new Map<string, Promise<IdAndName[]>>();
const emptyNames: IdAndName[] = [];

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error("Failed to load names");

const loadNames = (
  queryKey: string,
  query: NameListLoader,
): Promise<IdAndName[]> => {
  const pendingQuery = pendingNameListQueries.get(queryKey);
  if (pendingQuery) {
    return pendingQuery;
  }

  const promise = query()
    .finally(() => {
      if (pendingNameListQueries.get(queryKey) === promise) {
        pendingNameListQueries.delete(queryKey);
      }
    });

  pendingNameListQueries.set(queryKey, promise);
  return promise;
};

export const toNameOptions = (names: readonly IdAndName[]): LabelAndValueOption[] =>
  names
    .map(({ id, name }) => ({ label: name.trim(), value: id }))
    .sort((first, second) => first.label.localeCompare(second.label));

export type NameListQueryResult = {
  names: IdAndName[];
  options: LabelAndValueOption[];
  nameById: Map<number, string>;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

export const useNameListQuery = ({
  queryKey,
  query,
  enabled = true,
}: NameListQueryConfig): NameListQueryResult => {
  const [state, setState] = useState<NameListState>({
    names: emptyNames,
    isLoading: enabled,
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ names: emptyNames, isLoading: false, error: null });
      return;
    }

    let isActive = true;
    setState((current) => ({ ...current, isLoading: true, error: null }));
    loadNames(queryKey, query)
      .then((names) => {
        if (isActive) {
          setState({ names, isLoading: false, error: null });
        }
      })
      .catch((error: unknown) => {
        if (isActive) {
          setState({ names: emptyNames, isLoading: false, error: toError(error) });
        }
      });

    return () => {
      isActive = false;
    };
  }, [enabled, query, queryKey]);

  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, isLoading: true, error: null }));

    try {
      const names = await loadNames(queryKey, query);
      setState({ names, isLoading: false, error: null });
    } catch (error) {
      setState({ names: emptyNames, isLoading: false, error: toError(error) });
    }
  }, [query, queryKey]);

  const options = useMemo(() => toNameOptions(state.names), [state.names]);
  const nameById = useMemo(
    () => new Map(state.names.map(({ id, name }) => [id, name.trim()])),
    [state.names],
  );

  return { ...state, options, nameById, refresh };
};

export const resetNameListQueryCache = (): void => {
  pendingNameListQueries.clear();
};
